import * as station from "./api/station";
import AsyncInterval from "./AsyncInterval";
import {useEffect, useRef} from "react";

const inactivityMS =
    import.meta.env.REACT_APP_INACTIVITY_SECONDS !== undefined
        ? parseInt(import.meta.env.REACT_APP_INACTIVITY_SECONDS) * 1000
        : 30000;

const pollingIntervalMS = 300000; // 5 minutes

export default function useAppActivityManager(
    inactiveCallback: () => Promise<void>,
) {
    const isActive = useRef(false);
    const intervalRef = useRef<AsyncInterval>();
    const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        const createPollingInterval = () => {
            if (isActive.current) {
                station.updateStationStatus(false);
                isActive.current = false;
            }

            intervalRef.current = new AsyncInterval(
                inactiveCallback,
                pollingIntervalMS,
            );
        };

        const resetInterval = () => {
            if (!isActive.current) {
                isActive.current = true;
                station.updateStationStatus(true);
            }
            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current);
            }
            intervalRef.current?.cancel();
            inactivityTimeoutRef.current = setTimeout(
                createPollingInterval,
                inactivityMS,
            );
        };

        const events = ["mousedown", "scroll", "keydown"];
        events.forEach((e) => window.addEventListener(e, resetInterval));

        inactivityTimeoutRef.current = setTimeout(
            createPollingInterval,
            inactivityMS,
        );

        return () => {
            events.forEach((e) => {
                window.removeEventListener(e, resetInterval);
            });
            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current);
            }
            intervalRef.current?.cancel();
        };
    }, [inactiveCallback]);
}
